import React, { useState, useEffect } from "react";
import axios from "axios";
import Gauge3 from "./Gauge3";
import DemandResponsePlans from './DemandResponsePlans';
import { openResources } from "../authConfig";

interface DataItem {
    scheduleDay: string;
    propertyType: string;
    description: string;
    probability: number;
    expectedTime: string;
    dateTime: string;
}

interface SystemCondition {
    id: number;
    dateTime: string;
    percentage: number;
}

const App: React.FC = () => {
    const { data, loading, error } = useData();
    const { conditions, loadingConditions, errorConditions, fetchConditions } = useConditions();
    const [gaugeValue, setGaugeValue] = useState(0);

    useEffect(() => {
        fetchConditions();
    }, []);

    useEffect(() => {
        if (conditions.length > 0) {
            setGaugeValue(conditions[0]?.percentage);
        }
    }, [conditions]); // Reacts to changes in conditions


    useEffect(() => {
        const interval = setInterval(() => {
            fetchConditions();
            var value = conditions.length > 0 ? conditions[0]?.percentage : 0;
            if (value !== gaugeValue) {
                //console.log("Gauge value changed:", value);
                setGaugeValue(value);
            }
        }, 10000);
        
        return () => clearInterval(interval);
    }, [fetchConditions, conditions, gaugeValue]);
        

    if (loading) return <div>Loading...</div>;
    if (error) return <div>{error}</div>;
    if (loadingConditions) return <div>Loading...</div>;
    if (errorConditions) return <div>{error}</div>;

    // Filter data explicitly for "Today" and "Next Day"
    const todayData = data.filter(item => item.scheduleDay === "Today");
    const nextDayData = data.filter(item => item.scheduleDay === "Next Day");
    const futureData = data.filter(item => item.scheduleDay === "Future");

    if(todayData.length === 0 && nextDayData.length === 0) { // && futureData.length > 0
        todayData.push(futureData[0]);
        todayData.push(futureData[1]);
        todayData.push(futureData[2]);
        todayData.push(futureData[3]);

        nextDayData.push(futureData[4]);
        nextDayData.push(futureData[5]);
        nextDayData.push(futureData[6]);
        nextDayData.push(futureData[7]);
    }

    const renderGauge = (title: string, value: number, maxValue: number) => (
        <div>
          <Gauge3 title={title} value={value} maxValue={maxValue}/>
        </div>
      );
      
      const renderTable = (title: string, items: typeof data) => (
        <div style={{ width: "100%", marginBottom: "20px" }}>
            <h2>{title}</h2>
            {items.length === 0 ? (
                <p>No data available for {title}</p>
            ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "20px" }}>
                    <thead>
                        <tr style={{ backgroundColor: "#f2f2f2" }}>
                            <th style={{ border: "1px solid #ddd", padding: "8px" }}>Date</th>
                            <th style={{ border: "1px solid #ddd", padding: "8px" }}>Description</th>
                            <th style={{ border: "1px solid #ddd", padding: "8px" }}>Probability</th>
                            <th style={{ border: "1px solid #ddd", padding: "8px" }}>ExpectedTime</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => (
                            <tr key={index}>
                                <td style={{ border: "1px solid #ddd", padding: "8px" }}>{new Date(item?.dateTime).toLocaleDateString('en-US') || '--'}</td>
                                <td style={{ border: "1px solid #ddd", padding: "8px" }}>{item?.description || '--'}</td>
                                <td style={{ border: "1px solid #ddd", padding: "8px" }}>{item?.probability || '--'}</td>
                                <td style={{ border: "1px solid #ddd", padding: "8px" }}>{item?.expectedTime || '--'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
    
    return (
        <div className="responsive-container" /*style={{ display: "flex", flexDirection: "row"}}*/ >
            <div style={{ display: "flex", flexDirection: "column", flex: "1" }}>
                {/* Left Column: Gauge */}
                {renderGauge("Minnkota System Conditions", gaugeValue, 100)}
            </div>
            {/* Right Column: Tables */}
            <div style={{ display: "flex", flexDirection: "column", flex: "1"}}>
                {renderTable(new Date(todayData[0]?.dateTime || new Date()).toLocaleDateString('en-US'), todayData)}
                {renderTable(new Date(nextDayData[0]?.dateTime || addDays(new Date(), 1)).toLocaleDateString('en-US'), nextDayData)}
            </div>
            <div style={{ display: "flex", flexDirection: "column", flex: "1"}}>
                <DemandResponsePlans numPlans={1}/>
            </div>
        </div>
    );
};

const addDays = (date: Date, days: number): Date => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
};

export const useConditions = () => {
    const [conditions, setConditions] = useState<SystemCondition[]>([]);
    const [loadingConditions, setLoading] = useState<boolean>(true);
    const [errorConditions, setError] = useState<string | null>(null);

    const fetchConditions = async () => {
        //const baseUrl = "https://api.minnkota.com";
        //const endpoint = "/demandresponse/loadmanagement/systemcondition";
        //`${baseUrl}${endpoint}`
        try {
            const response = await axios.get(openResources.systemCondition.getEndpoint);
            //console.log("Response data:", response.data); // Debugging line
            setConditions(response.data); 
        } catch (error: any) {
            setError(`Error fetching conditions: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchConditions();
    }, []);

    return { conditions, loadingConditions, errorConditions, fetchConditions }; // Ensure fetchConditions is returned
};

export const useData = () => {
    const [data, setData] = useState<DataItem[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchData = async () => {
        //const baseUrl = "https://api.minnkota.com";
        //const endpoint = "/demandresponse/loadmanagement/currentschedule";

        try {
            //`${baseUrl}${endpoint}`
            const response = await axios.get(openResources.currentSchedule.endpoint);
            const adaptedData: DataItem[] = response.data.map((item: any) => ({
                propertyType: item.propertyType,
                description: item.description,
                probability: item.probability,
                expectedTime: item.expectedTime,
                dateTime: item.dateTime.toString("mm/dd/yyyy"),
                scheduleDay: item.scheduleDay,
            }));
            setData(adaptedData);
        } catch (error: any) {
            setError(`Error fetching data: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    return { data, loading, error };
};

export default App;